'use client';

import { useEffect, useRef, useState } from 'react';

import { ChevronLeft, ChevronRight, X } from 'lucide-react';

import { cn } from '@/lib/utils';

import type { WorksheetPhoto } from './worksheetPhoto';

const controlClassName =
  'inline-flex size-11 items-center justify-center rounded-full bg-white/10 text-white transition-transform duration-150 ease-[cubic-bezier(0.2,0,0,1)] hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white active:scale-[0.96] disabled:pointer-events-none disabled:opacity-40 motion-reduce:transition-none motion-reduce:active:scale-100';

export function PhotoLightbox({
  photos,
  index,
  onIndex,
  onClose,
}: {
  photos: WorksheetPhoto[];
  index: number;
  onIndex: (index: number) => void;
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const photo = photos[index];
  const [frame, setFrame] = useState<'full' | 'thumb' | 'failed'>('full');
  const count = photos.length;
  const label = photo?.label;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    // StrictMode runs setup, cleanup, setup on the same dialog. close()
    // queues its event, so a listener attached by the second setup still
    // receives the cleanup's close and clears the gallery. Leave the dialog
    // open across that extra cleanup, and ignore close events from this turn.
    let ready = false;
    const handleClose = () => {
      if (!ready) return;
      onClose();
    };
    dialog.addEventListener('close', handleClose);
    if (!dialog.open) dialog.showModal();
    const readyId = window.setTimeout(() => {
      ready = true;
    }, 0);

    return () => {
      ready = false;
      window.clearTimeout(readyId);
      dialog.removeEventListener('close', handleClose);
      if (dialog.open) dialog.close();
    };
  }, [onClose]);

  useEffect(() => {
    setFrame('full');
  }, [photo?.url]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        onIndex(Math.min(count - 1, index + 1));
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        onIndex(Math.max(0, index - 1));
      }
    };

    dialog.addEventListener('keydown', onKey);
    return () => dialog.removeEventListener('keydown', onKey);
  }, [count, index, onIndex]);

  if (!photo) return null;

  const accessibleName = label
    ? `${label}. Photo ${index + 1} of ${count}`
    : `Photo ${index + 1} of ${count}`;
  const thumbFallback =
    photo.thumbnailUrl !== '' && photo.thumbnailUrl !== photo.url;
  const showingThumb = frame === 'thumb' && thumbFallback;

  return (
    <dialog
      ref={dialogRef}
      aria-label={accessibleName}
      data-cy='worksheet-photo-lightbox'
      className='m-0 h-dvh w-screen max-h-none max-w-none border-0 bg-black p-0 text-white backdrop:bg-black'
    >
      <div className='flex h-full flex-col overscroll-contain'>
        <div className='flex items-center justify-between gap-3 px-4 py-3'>
          <a
            href={photo.url}
            target='_blank'
            rel='noopener noreferrer'
            data-cy='worksheet-photo-open-original'
            className='text-sm text-white/80 underline decoration-white/30 underline-offset-2 hover:text-white hover:decoration-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white'
          >
            Open original
          </a>
          <p className='tabular-nums text-sm text-white/80'>
            {index + 1} of {count}
          </p>
          <button
            type='button'
            autoFocus
            data-cy='worksheet-photo-close'
            aria-label='Close photo'
            onClick={onClose}
            className={controlClassName}
          >
            <X size={20} strokeWidth={1.5} />
          </button>
        </div>

        <div
          className='relative flex min-h-0 flex-1 items-center justify-center px-16'
          onClick={event => {
            if (event.target === event.currentTarget) onClose();
          }}
        >
          {count > 1 ? (
            <button
              type='button'
              data-cy='worksheet-photo-prev'
              aria-label='Previous photo'
              disabled={index === 0}
              onClick={() => onIndex(index - 1)}
              className={cn(controlClassName, 'absolute left-3')}
            >
              <ChevronLeft size={20} strokeWidth={1.5} />
            </button>
          ) : null}

          {frame === 'failed' ? (
            <p className='max-w-sm text-center text-sm text-white/80'>
              This photo could not be loaded.
            </p>
          ) : (
            <img
              key={showingThumb ? photo.thumbnailUrl : photo.url}
              src={showingThumb ? photo.thumbnailUrl : photo.url}
              alt={label ?? ''}
              onError={() => {
                if (frame === 'full' && thumbFallback) {
                  setFrame('thumb');
                  return;
                }
                setFrame('failed');
              }}
              className='max-h-full max-w-full object-contain outline outline-1 -outline-offset-1 outline-white/10'
            />
          )}

          {count > 1 ? (
            <button
              type='button'
              data-cy='worksheet-photo-next'
              aria-label='Next photo'
              disabled={index >= count - 1}
              onClick={() => onIndex(index + 1)}
              className={cn(controlClassName, 'absolute right-3')}
            >
              <ChevronRight size={20} strokeWidth={1.5} />
            </button>
          ) : null}
        </div>

        {label ? (
          <p className='mx-auto max-w-lg px-6 pb-6 text-center text-sm leading-6 text-white/80'>
            {label}
          </p>
        ) : (
          <div className='h-6' />
        )}
      </div>
    </dialog>
  );
}
